/**
 * RELEASES & STAGES (EXPERT-ROOMS.md §15) — on an Expert Network's Settings. The expert trains in dev (live); "Cut a
 * release" freezes what dev has learned (rules, procedures, trained answers) into an immutable release; each stage
 * (QA, Production) runs the release promoted to it — promote a newer one, or an older one to roll back. Production
 * answers the other workspaces of the company. Creator / workspace admins act (``network.canChangeAccess``).
 * FOUR EYES: a promotion to Production is a REQUEST another eligible person approves (never the requester); rolling
 * back to a release Production already ran is direct. A workspace OWNER may switch an expert to single-person.
 */
import React, { useState } from 'react';
import ExpertProdReleaseDialog from './ExpertProdReleaseDialog.jsx';
import { getRestApiBearerToken } from '@/services/apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const LABEL = { dev: 'Training (dev)', qa: 'QA', prod: 'Production' };

const knownGood = (st, v) => {
  const h = (st && st.history) || [];
  const seen = new Set(h.flatMap((x) => [Number(x.version || 0), Number(x.from || 0)]).filter(Boolean));
  return seen.has(Number(v)) && Number(v) !== Number(st.version);
};

export default function ExpertReleasesSection({ network, onNetworkUpdate, workspaceRole = '', me = '', onDeployStage = null,
  onSyncStage = null, onSubmitToRegistry = null, hideTitle = false }) {
  const xr = network?.expertReleases;
  const [prodAsk, setProdAsk] = useState(null);           // {version, asks} — the Release-to-Production dialog
  const [notes, setNotes] = useState('');
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!network?.expertNetwork || !xr) return null;
  const canAct = !!network.canChangeAccess;
  const releases = xr.releases || [];
  const twoPerson = (xr.prodApproval || 'two_person') === 'two_person';
  const pending = (xr.requests || []).filter((r) => r.status === 'pending');
  const isOwner = String(workspaceRole || '').toUpperCase() === 'OWNER';

  const call = async (req) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(network.networkId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ expertReleases: req }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
      const view = body.expertReleases || body;
      onNetworkUpdate?.({ ...network, expertReleases: view });
      if (req.action === 'cut') setNotes('');
      // a promote makes the stage REAL: no team yet → deploy it (one click, prefilled); a team on an older network
      // version → move its rooms to the version the release was cut on
      const st = req.action === 'promote' ? req.stage
        : req.action === 'approve' ? (pending.find((r) => r.requestId === req.requestId) || {}).stage : '';
      const now = st ? (view.stages || []).find((x) => x.stage === st) : null;
      if (now && now.version) {
        if (!now.deployed) onDeployStage?.(st);
        else if (now.teamInSync === false) onSyncStage?.(st, now);
      }
      // "also the public registry" → submit the release the stage runs to the curated registry (reviewed there)
      if (st === 'prod' && view.listing?.visibility === 'registry' && now?.networkRelease) {
        onSubmitToRegistry?.({ releaseVersion: now.networkRelease, label: now.label });
      }
      return true;
    } catch (e) {
      setErr(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={hideTitle ? 'space-y-4' : 'bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4'} data-testid="expert-releases">
      {hideTitle ? null : <h3 className="text-sm font-semibold text-gray-800">Releases &amp; stages</h3>}
      <p className="text-xs text-gray-500">
        The expert learns in Training. A release freezes what it has learned; QA and Production run exactly the release
        you promote to them — and change only when you promote again. Production answers the other workspaces.
      </p>
      <table className="w-full text-xs" data-testid="expert-stages">
        <tbody className="divide-y divide-gray-100">
          {(xr.stages || []).map((s) => (
            <tr key={s.stage} data-testid={`expert-stage-${s.stage}`}>
              <td className="py-1.5 font-medium text-gray-800">{LABEL[s.stage] || s.stage}</td>
              <td className="py-1.5 text-gray-600">
                {s.live ? `${s.label && s.label !== 'live' ? `${s.label} — ` : 'live — '}where the expert learns`
                  : s.version ? `release ${s.label || `v${s.version}`}` : 'nothing promoted yet'}
                {s.deployed ? ' · team running' : ''}
                {s.deployed && s.teamInSync === false ? (
                  <span className="ml-1 text-amber-700" data-testid={`expert-stage-behind-${s.stage}`}>
                    · its rooms run an older network version
                    {onSyncStage && canAct ? (
                      <button type="button" data-testid={`expert-stage-sync-${s.stage}`} onClick={() => onSyncStage(s.stage, s)}
                        className="ml-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-600 text-white hover:bg-amber-700">
                        Move the team to {s.networkLabel || 'its version'}
                      </button>
                    ) : null}
                  </span>
                ) : null}
                {/* a promoted release serves NOTHING until its stage has a team of rooms — say so, one click away */}
                {!s.live && s.version && !s.deployed ? (
                  <span className="ml-1 text-amber-700" data-testid={`expert-stage-noteam-${s.stage}`}>
                    · no team yet
                    {onDeployStage && canAct ? (
                      <button type="button" data-testid={`expert-stage-deploy-${s.stage}`} onClick={() => onDeployStage(s.stage)}
                        className="ml-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-600 text-white hover:bg-amber-700">
                        Deploy the {LABEL[s.stage] || s.stage} team
                      </button>
                    ) : null}
                  </span>
                ) : null}
              </td>
              <td className="py-1.5 text-right">
                {!s.live && canAct && releases.length ? (
                  <span className="inline-flex items-center gap-1">
                    <select value={pick[s.stage] || ''} onChange={(e) => setPick({ ...pick, [s.stage]: e.target.value })}
                      aria-label={`Release for ${LABEL[s.stage]}`} data-testid={`expert-stage-pick-${s.stage}`}
                      className="rounded border border-gray-300 px-1 py-0.5 text-xs">
                      <option value="">Choose a release…</option>
                      {releases.map((r) => (
                        <option key={r.version} value={r.version}>
                          {r.label || `v${r.version}`}{r.version === s.version ? ' (current)' : r.version < s.version ? ' (roll back)' : ''}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const asks = s.stage === 'prod' && twoPerson && pick[s.stage] && !knownGood(s, pick[s.stage]);
                      // §14b — Production waits until the release's subscription form passed validation
                      const formBlocked = s.stage === 'prod' && pick[s.stage]
                        && releases.find((r) => r.version === Number(pick[s.stage]))?.formReady === false;
                      return (
                        <>
                        {formBlocked ? (
                          <span className="text-[11px] text-amber-700" data-testid="expert-stage-form-blocked">
                            Validate its subscription form first (Rules → Subscription form)
                          </span>
                        ) : null}
                        <button type="button" disabled={busy || !pick[s.stage] || Number(pick[s.stage]) === s.version
                          || (asks && pending.length > 0) || formBlocked}
                          className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
                          data-testid={`expert-stage-promote-${s.stage}`}
                          onClick={() => (s.stage === 'prod'
                            ? setProdAsk({ version: Number(pick[s.stage]), asks: !!asks })
                            : call({ action: 'promote', stage: s.stage, version: Number(pick[s.stage]) }))}>
                          {asks ? 'Ask for approval' : 'Promote'}
                        </button>
                        </>
                      );
                    })()}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ExpertProdReleaseDialog open={!!prodAsk} inline network={network} busy={busy} asks={!!prodAsk?.asks}
        shared={!!network?.companySharing?.published}
        release={prodAsk ? releases.find((r) => r.version === prodAsk.version) : null}
        onCancel={() => setProdAsk(null)}
        onRelease={async (publish) => {
          const ok = await call({ action: 'promote', stage: 'prod', version: prodAsk.version, publish });
          if (ok) setProdAsk(null);
        }} />
      {pending.map((r) => (
        <div key={r.requestId} className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs" data-testid={`promotion-request-${r.requestId}`}>
          <span className="font-medium text-amber-900">
            {r.by && r.by === me ? 'You asked' : `${r.byName || 'A teammate'} asks`} to put release {(releases.find((x) => x.version === r.version) || {}).label || `v${r.version}`} in Production
          </span>
          {r.from ? <span className="text-amber-800"> (now v{r.from})</span> : null}
          {r.reason ? <span className="text-amber-800"> — {r.reason}</span> : null}
          {canAct ? (
            <span className="ml-2 inline-flex gap-1">
              {r.by !== me || !twoPerson ? (
                <>
                  <button type="button" disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
                    data-testid={`promotion-approve-${r.requestId}`} onClick={() => call({ action: 'approve', requestId: r.requestId })}>
                    Approve and promote
                  </button>
                  <button type="button" disabled={busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
                    data-testid={`promotion-reject-${r.requestId}`} onClick={() => call({ action: 'reject', requestId: r.requestId })}>
                    Reject
                  </button>
                </>
              ) : (
                <button type="button" disabled={busy} className={`${btn} text-gray-600 hover:bg-gray-100`}
                  data-testid={`promotion-cancel-${r.requestId}`} onClick={() => call({ action: 'cancel', requestId: r.requestId })}>
                  Cancel my request
                </button>
              )}
            </span>
          ) : null}
        </div>
      ))}
      <p className="text-[11px] text-gray-500" data-testid="prod-approval-mode">
        {twoPerson ? 'Production changes need a teammate to approve (rolling back to a release Production already ran doesn\'t).'
          : 'Production changes are made by one person.'}
        {' '}This is the workspace&apos;s rule — {isOwner ? 'change it in' : 'a workspace owner changes it in'}{' '}
        <a href="/workspace/settings" className="text-indigo-700 hover:underline" data-testid="prod-approval-link">
          Workspace settings → Tenant</a>.
      </p>
      {canAct ? (
        <div className="flex flex-wrap items-center gap-2">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changed (optional)"
            maxLength={500} data-testid="expert-release-notes"
            className="min-w-[14rem] flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs" />
          <button type="button" disabled={busy} data-testid="expert-release-cut"
            className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}
            onClick={() => call({ action: 'cut', notes })}>
            Cut a release from Training
          </button>
        </div>
      ) : null}
      {releases.length ? (
        <ul className="divide-y divide-gray-100 text-xs" data-testid="expert-release-list">
          {releases.slice(0, 8).map((r) => (
            <li key={r.version} className="py-1 text-gray-600">
              <span className="font-medium text-gray-800">{r.label || `v${r.version}`}</span> · {r.teams} teams · {r.rules} rules ·
              {' '}{r.trained} trained answers{r.notes ? ` — ${r.notes}` : ''}
            </li>
          ))}
        </ul>
      ) : <p className="text-xs text-gray-400">No releases yet.</p>}
      {err ? <p className="text-xs text-rose-600">{err}</p> : null}

    </section>
  );
}
